import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardFooter,
  Typography,
  Button,
  Spinner
} from "@material-tailwind/react";
import FullScreenSpinner from "../Spinner/FullScreenSpinner";
import DialogWithForm from "./ModalProductDetails";
import { Notification } from "../Notification/Notification";
import { Axios } from '../Axios/Axios';

const axios = new Axios();

export default function ProductDetails() {
  const location = useLocation();
  const { productId } = useParams(); // Get productId from URL params
  const { product: locationProduct } = location.state || {};
  
  const [product, setProduct] = useState(locationProduct || null);
  const [animate, setAnimate] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [error, setError] = useState(null);

  // Fetch product if not available from location state
  // Fetch product if not available from location state
useEffect(() => {
  const fetchProduct = async () => {
    console.log("Fetching product with ID:", productId);
    console.log("Current product state:", product);
    if (product == null && productId) {
      setLoading(true);
      try {
        let url = `api/home/get-product/${productId}`;
        const response = await axios.get(url);
        
        // Axios automatically throws an error for non-2xx status codes
        // So if we reach here, the request was successful
        // Access data using response.data, not response.json()
        const productData = response.data;
        
        // Since your backend returns an array, get the first item
        if (Array.isArray(productData) && productData.length > 0) {
          setProduct(productData[0]);
        } else if (!Array.isArray(productData)) {
          setProduct(productData);
        } else {
          throw new Error("No product data found");
        }
        
      } catch (err) {
        console.error("Error fetching product:", err);
        
        // Handle different types of errors
        if (err.response) {
          // Server responded with error status
          console.log("Error status:", err.response.status);
          console.log("Error data:", err.response.data);
          setError(`Failed to load product details: ${err.response.status}`);
        } else if (err.request) {
          // Request was made but no response received
          console.log("No response received:", err.request);
          setError("Network error - please check your connection");
        } else {
          // Something else happened
          console.log("Error:", err.message);
          setError("Failed to load product details");
        }
        
        handleNotification("Failed to load product details", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  fetchProduct();
}, [productId, product]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const timer = setTimeout(() => setAnimate(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => setNotification({ message: "", type: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleNotification = (message, type) => {
    setNotification({ message, type });
  };

  const toggleModal = () => setModalOpen(prev => !prev);

  // Show loading spinner while fetching product
  if (loading) {
    return <FullScreenSpinner display={true} />;
  }

  // Show error message if product fetch failed
  if (error && !product) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Typography variant="h5" color="red" className="mb-4">
            {error}
          </Typography>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Show message if no product found
  if (!product) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Typography variant="h5" color="gray">
          Product not found
        </Typography>
      </div>
    );
  }

  // Parse specifications safely
  const specifications = product.specifications 
    ? JSON.parse(product.specifications) 
    : {};

  return (
    <div>
      <div 
        style={{ background: "#f7f7f7" }}
        className="pb-10 mx-auto w-full h-fit lg:h-[90.5vh] flex items-center justify-center"
      >
        <div
          className={`px-4 sm:px-8 md:px-16 mx-auto w-full max-w-screen-sm sm:max-w-screen-md lg:max-w-[95%] xl:max-w-[90%] ${
            animate ? "translate-y-0 opacity-100" : "-translate-y-10 opacity-0"
          } transition-all duration-500 ease-out`}
        >
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10 lg:gap-40 2xl:gap-80">
            {/* Image Section */}
            <div className="w-full md:w-full lg:w-2/5 relative flex justify-center">
              {!isLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                  <Spinner />
                </div>
              )}
              <img
                src={`https://samsung-climatesolutions.com/${product.image_url}`}
                alt={product.name}
                onLoad={() => setIsLoaded(true)}
                onError={() => setIsLoaded(true)}
                className={`transition-opacity duration-500 w-full ${
                  isLoaded ? "opacity-100" : "opacity-0"
                }`}
              />
            </div>

            {/* Card Section */}
            <Card className="max-w-[450px] w-full md:w-full lg:w-1/3 xl:1/3 xl:h-fit flex justify-center">
              <CardBody>
                <Typography
                  variant="h5"
                  color="black"
                  className="mb-10 text-center lg:text-left"
                >
                  {product.name}
                </Typography>
                <Typography variant="h6" className="text-center lg:text-left">
                  Достапни капацитети:
                </Typography>
                <Typography className="ml-2 mb-7 font-normal text-center lg:text-left">
                  {product.capacities}
                </Typography>
                <Typography variant="h6" className="text-center lg:text-left">
                  {product.category} | {product.sub_category}
                </Typography>
              </CardBody>
              <CardFooter className="pt-0">
                <Button 
                  onClick={toggleModal} 
                  variant="outlined" 
                  fullWidth={true}
                >
                  Испрати барање
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>

      <div
        style={{ background: "#eaeaea" }}
        className="py-16 px-4 sm:px-8 md:px-16 mx-auto max-w-screen-sm sm:max-w-screen-md lg:max-w-[95%] xl:max-w-[90%] mt-32 rounded-lg"
      >
        <Typography 
          className="mb-10 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-center"
        >
          Спецификации за производот
        </Typography>

        {specifications && Object.keys(specifications).length > 0 ? (
          Object.keys(specifications).map((field) => (
            <div key={field}>
              <h2 className="text-2xl font-bold">{field}</h2>
              {specifications[field].map((obj, index, array) => {
                if (index % 2 === 0) {
                  return (
                    <div key={index} className="my-4 p-4 border-b">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="items-center mb-14">
                          <h3 className="text-xl font-bold">{obj.title}</h3>
                          <p className="text-lg ml-2 mt-1">{obj.value}</p>
                        </div>
                        {array[index + 1] && (
                          <div className="items-center">
                            <h3 className="text-xl font-bold">
                              {array[index + 1].title}
                            </h3>
                            <p className="text-lg ml-2 mt-1">
                              {array[index + 1].value}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
              <hr className="mb-5 h-0.5 border-t-0 bg-neutral-100 dark:bg-black/10" />
            </div>
          ))
        ) : (
          <Typography className="text-center text-gray-600">
            No specifications available for this product.
          </Typography>
        )}
      </div>

      <FullScreenSpinner display={loading} />
      
      {/* Render the modal here */}
      <DialogWithForm 
        updateNotification={handleNotification}  
        open={modalOpen} 
        handleOpen={toggleModal} 
        product={product} 
      />
      {notification.message && notification.type && (
        <Notification message={notification.message} type={notification.type} />
      )}
    </div>
  );
}
